/* What each column of this dataset actually looks like, and how it moved.
 *
 * §9 records this gap in its own words — *"the profiler has no screen, and the drift report is the one
 * that most needs one"* — so the finding a profiler exists to produce ("this column is 98% one value",
 * "this column's values turned over while its count did not") was reachable only by curl.
 *
 * Rendered inside `DatasetDetail` rather than as its own route, because a profile is *about* a dataset
 * and a separate screen would need a dataset picker duplicating the catalog. The slice is its own, so
 * the profiler's logic stays out of the catalog feature.
 *
 * **Every figure says whether it was measured.** `exact` travels on every row for the reason the
 * contract gives: the same 3% movement is a finding when measured and noise inside the estimator's
 * error bar. A theta sketch at k=1024 carries ~3% relative standard error, so presenting an estimate
 * as a measurement invites somebody to act on the error bar.
 *
 * **A null is "not measured", never a zero.** `completeness` is null for an *empty relation* and a
 * measured 0.0 for an all-null column — two facts the API deliberately separates, and `0%` for both
 * would erase the distinction the platform went out of its way to keep.
 *
 * **There is a profile button, and this paragraph used to deny it.** It said "no profile button…
 * the panel names the command instead" while the empty state below already rendered one — the
 * claim-without-mechanism defect, stated in the file the mechanism lives in.
 *
 * The reasoning behind the old claim was right and is what the button is now bounded by. A profile is
 * up to 20 full `GROUP BY` scans of a customer's production table, and S-25 records a double-clicked
 * button buying 40 of them because each click minted a fresh idempotency key. So: the control is
 * `disabled` while a request is in flight, which closes the double-click; `useRequestProfile` holds
 * one key per dataset and clears it only on success, so a retry after a failure is the same decision
 * rather than a second one; and the cost is on the control's own title and in the footnote under the
 * panel rather than left for a reader to discover.
 *
 * **What is still deliberately absent is a profile-*everything* action.** That is the affordance the
 * old paragraph was really about — one press against an estate is unbounded in a way one press
 * against a table is not — and the footnote says so.
 *
 * **No value is shown anywhere, and nothing here renders a digest.** The API returns salted digests
 * and counts, and the `preview` field a reader might expect was removed as rung-4 data on a rung-3
 * surface. `frequent_values[].digest` *is* on the wire and in the type this file imports, so the
 * accurate claim is that no column renders one rather than that there is nothing to render — §9's own
 * history is that a `preview` lived beside it for exactly that reason. A salted digest is rung 3, so
 * showing one would be a design question rather than a disclosure defect; not showing one is still
 * the right answer, because a digest tells a reader nothing they can act on.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ColumnDrift, ColumnSketch } from '@/shared/api/generated/catalog';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { useRequestProfile, useSketchDrift, useSketches } from '../api/profiler';
import {
  DRIFT_TONE,
  count,
  distinctReading,
  dominantReading,
  driftVerdict,
  isDominated,
  profileHighlights,
  share,
} from '../model/presentation';
import { BaselineSummary } from './BaselineSummary';
import { DimensionReadout } from './DimensionReadout';

export interface ProfilePanelProps {
  readonly datasetId: string;
  /** Columns in the current schema version, or `undefined` when the caller does not know.
   *
   * Only used to decide when a running profile has finished. `undefined` disables polling
   * altogether, which is the honest default: without a total there is no way to say a profile is
   * complete, and an interval that cannot end is worse than no progress at all. */
  readonly expectedColumns?: number | undefined;
}

export function ProfilePanel({
  datasetId,
  expectedColumns,
}: ProfilePanelProps): ReactElement {
  const { t } = useTranslation();
  const sketches = useSketches(datasetId, expectedColumns);
  const drift = useSketchDrift(datasetId);
  const requestProfile = useRequestProfile(datasetId);

  /* How many columns are durable right now, and how many a complete profile would have. Both come
     from data already on the page — no operation table, no synthetic percentage. */
  const stored = sketches.data?.columns.length ?? 0;
  const progress =
    expectedColumns === undefined || expectedColumns <= 0
      ? 0
      : Math.min(1, stored / expectedColumns);
  /* **Shown while a request has been made and the count is short**, so it ends on exactly the
     condition the poll ends on. Deliberately not "while the mutation is pending": the POST is a 202
     that returns in milliseconds, so a spinner tied to it would vanish before the first column was
     scanned — which is the state this whole readout exists to make visible. */
  const profiling =
    requestProfile.isSuccess && expectedColumns !== undefined && stored < expectedColumns;

  if (sketches.isPending) {
    return (
      <p role="status" className="lq-muted">
        {t('profile.loading')}
      </p>
    );
  }

  if (sketches.isError) {
    return <Alert tone="error">{sketches.error.message}</Alert>;
  }

  if (sketches.data === null) {
    /* Never profiled is the normal state of a freshly harvested dataset. The Run Profile button allows the user to compute it. */
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
          alignItems: 'flex-start',
        }}
      >
        {requestProfile.isError && (
          <Alert tone="error">{requestProfile.error.message}</Alert>
        )}
        {/* **Progress, and every part of it is a count rather than a clock.** `capture` appends
            and commits one column at a time, so `stored` genuinely rises as scans finish; the
            denominator is the current version's column count, which is what "finished" means. The
            bar therefore moves when work completes and stalls when work stalls — which is the
            point, because a bar that advanced on a timer would tell a reader a profile was
            progressing while it sat blocked on a warehouse.

            Rendered while a request has been made and the stored count is short of the total, so it
            disappears by itself: the poll and this notice end on the same condition, which is why
            neither needs clearing. */}
        {profiling ? (
          <div className="lq-profiling" role="status">
            <div className="lq-profiling__head">
              <span>{t('profile.runningDetail')}</span>
              <span className="lumiq-numeric">
                {t('profile.runningCount', { stored, total: expectedColumns })}
              </span>
            </div>
            <div className="lq-profiling__track">
              <div
                className="lq-profiling__fill"
                style={{ width: `${String(Math.round(progress * 100))}%` }}
              />
            </div>
          </div>
        ) : (
          requestProfile.isSuccess && (
            <Alert tone="notice">{t('profile.runSuccess')}</Alert>
          )
        )}
        {/* **"No profile for the current schema version", not "not profiled yet"**, and the
            difference was found by driving the two screens against real data rather than by reading
            either one.

            `sketches` resolves the dataset's *latest* version and reads that version's sketches, so
            a table profiled before its schema last changed answers `null` here. The dataset listing
            asks a different question — `GET /datasets/facts` reports the most recent profile of any
            version, deliberately, because a schema change is not an erasure of what was measured
            before it. Both are correct; the old wording was the one that read as absolute, so one
            screen said "profiled Aug 24" while this one said "not profiled yet" about the same
            table. A contradiction between two screens costs a reader their trust in both. */}
        <EmptyState
          title={t('profile.never.title')}
          guidance={t('profile.never.guidance')}
        />
        <Button
          tone="primary"
          busy={requestProfile.isPending}
          onClick={() => {
            requestProfile.mutate();
          }}
        >
          {requestProfile.isPending ? t('profile.running') : t('profile.run')}
        </Button>
      </div>
    );
  }

  const columns = sketches.data.columns;
  /* **Read from `isSuccess`, not from `data`, and that distinction was found by forcing a 500.**
   * React Query keeps the previous `data` alongside `isError` by design, so building the map from
   * `drift.data` rendered four confident verdicts *underneath* a banner saying the comparison could
   * not be read — an internal contradiction on the page, and a verdict presented as current for a
   * comparison that had just failed. Every row reads "not compared" now and the banner is the only
   * statement about drift, which is the same rule the footer follows one element down. */
  const highlights = profileHighlights(columns);
  const driftByColumn = new Map<string, ColumnDrift>(
    (drift.isSuccess ? (drift.data?.columns ?? []) : []).map((row) => [row.column, row]),
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--space-3)',
        }}
      >
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('profile.computedAt', {
            /* Formatted here rather than in the message, matching the schema history on this same
               screen. `i18next-icu` cannot render ICU's `time` type — see `copy.test.ts` — so a
               message spelling the clock time comes back as its own source. */
            at: new Date(sketches.data.computed_at).toLocaleString(),
          })}
        </p>
        <Button
          tone="secondary"
          busy={requestProfile.isPending}
          onClick={() => {
            requestProfile.mutate();
          }}
        >
          {requestProfile.isPending ? t('profile.running') : t('profile.run')}
        </Button>
      </div>

      {requestProfile.isError && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <Alert tone="error">{requestProfile.error.message}</Alert>
        </div>
      )}
      {requestProfile.isSuccess && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <Alert tone="notice">{t('profile.runSuccess')}</Alert>
        </div>
      )}

      {/* **The profile baseline, and everything it is subtracted from.**
          `core.catalog`'s `profile_baseline` explains why this is subtractive rather than an
          average of dimension ratios, and it is computed there rather than here so the score
          listing can carry the same figure without a sketch read per row. What belongs here is
          the reading. Three things are on screen together on purpose:
          the figure, the word *provisional*, and the list it decomposes into. A figure without the
          list is a number a steward cannot argue with, and one without the word invites it to be
          read as the dataset's quality score, which it is not — a rule score is that, and this
          demotes to a hint about which rules to write the moment one exists. */}
      <BaselineSummary baseline={sketches.data.baseline} />

      {/* **Six dials, below the figure they do not feed.** The baseline deducts only for
          completeness and cardinality shape; these say what the profile knows about all six
          dimensions. Colour separates measured from needs-a-rule from not-obtainable, and never
          encodes good or bad — see `model/dimensions.ts` for why uniqueness is shown and not
          scored. */}
      <DimensionReadout columns={columns} />

      {/* **Four cards for the four findings, derived rather than hand-picked.** See
          `profileHighlights` for which four and why: the reference this follows names three specific
          customer columns, which is a screenshot's choice and cannot be generalised — this platform
          does not know which of a customer's columns matters.

          A slot with no finding renders *nothing* rather than a card reading "—". A dataset of unique
          keys has no dominated column, and a card announcing the least-unique of them would be noise
          dressed as a finding. */}
      <div className="lq-kpi">
        {highlights.leastComplete !== null ? (
          <HighlightCard
            label={t('profile.highlight.completeness', {
              column: highlights.leastComplete.column,
            })}
            value={share(highlights.leastComplete.completeness)}
            note={t('profile.highlight.completenessNote', {
              at: new Date(sketches.data.computed_at).toLocaleDateString(),
            })}
          />
        ) : null}
        {highlights.unmeasured !== null ? (
          <HighlightCard
            label={t('profile.highlight.unmeasured', {
              column: highlights.unmeasured.column,
            })}
            /* The words, not a percentage. An empty relation has no completeness, and `0%` here
               would report a measured absence of data where the truth is an absence of measurement. */
            value={t('profile.highlight.unmeasuredValue')}
            note={t('profile.highlight.unmeasuredNote')}
            muted
          />
        ) : null}
        {highlights.widest !== null ? (
          <HighlightCard
            label={t('profile.highlight.distinct', { column: highlights.widest.column })}
            /* `distinctReading` carries the value *and* whether it was counted, so the card reads
               both off one call — two helpers would be two places the exactness marker is decided,
               and the note is the only thing that makes `≈ 41,203` interpretable. */
            value={distinctReading(highlights.widest).value}
            note={
              distinctReading(highlights.widest).measured
                ? t('profile.highlight.distinctExact')
                : t('profile.highlight.distinctEstimated')
            }
          />
        ) : null}
        {highlights.mostDominated !== null ? (
          <HighlightCard
            label={t('profile.highlight.dominant', {
              column: highlights.mostDominated.column,
            })}
            value={share(highlights.mostDominated.dominant_share)}
            note={
              isDominated(highlights.mostDominated)
                ? t('profile.highlight.dominantNotable')
                : t('profile.highlight.dominantOrdinary')
            }
          />
        ) : null}
      </div>

      {/* **Drift as a list of sentences rather than a column in the table**, which is what the
          reference does and is right on the merits: a verdict needs a sentence to be actionable, and
          a sentence does not fit a table cell beside six numbers. The table below still carries every
          column's figures — dropping it to match a screenshot would lose the data the screenshot was
          cropped above. */}
      {drift.isSuccess && drift.data !== null && drift.data.columns.length > 0 ? (
        <section className="lq-driftlist" aria-labelledby="drift-heading">
          <h3 id="drift-heading" className="lq-driftlist__heading">
            {t('profile.drift.heading')}
          </h3>
          <Card flush>
            <ul className="lq-driftlist__rows">
              {drift.data.columns.map((row) => (
                <DriftRow key={row.column} drift={row} />
              ))}
            </ul>
          </Card>
        </section>
      ) : null}

      <DataTable
        columns={[
          { key: 'column', label: t('profile.column.name') },
          { key: 'rows', label: t('profile.column.rows'), numeric: true },
          { key: 'nullCount', label: t('profile.column.nullCount'), numeric: true },
          { key: 'complete', label: t('profile.column.complete'), numeric: true },
          { key: 'distinct', label: t('profile.column.distinct'), numeric: true },
          { key: 'dominant', label: t('profile.column.dominant'), numeric: true },
          { key: 'drift', label: t('profile.column.drift') },
        ]}
        caption={t('profile.caption')}
      >
        {columns.map((sketch) => (
          <ColumnRow
            key={sketch.column}
            sketch={sketch}
            drift={driftByColumn.get(sketch.column)}
          />
        ))}
      </DataTable>

      {/* **Four states, and the first version collapsed three of them into a claim about the data.**
          A 404 genuinely means there is no baseline; a 500, a 429 or a still-pending request mean
          nothing about drift at all. Reporting them as "nothing has drifted yet" is the third
          instance of the class §9 records — the anomaly screen making every row say "not in the
          current catalogue listing" on a failed lookup, and the evals screen emptying every verdict
          column in silence — on the panel whose whole subject is not confusing absence with
          measurement. This panel adds two GETs per dataset view and `/v1/catalog` shares a rate
          limit, so a 429 here is the realistic trigger rather than the exotic one. */}
      {drift.isPending ? (
        <p className="lq-muted" style={{ marginTop: 8 }} role="status">
          {t('profile.drift.loading')}
        </p>
      ) : drift.isError ? (
        <Alert tone="warning" title={t('profile.drift.failed')}>
          {drift.error.message}
        </Alert>
      ) : drift.data === null ? (
        <p className="lq-muted" style={{ marginTop: 8 }}>
          {t('profile.drift.none')}
        </p>
      ) : (
        <p className="lq-muted" style={{ marginTop: 8 }}>
          {/* The platform's own sentence, verbatim. It is the property that makes this endpoint cheap
              and it is not obvious from a payload of numbers — a reader would reasonably assume a
              comparison re-read the source. */}
          {t('profile.drift.computedFrom', { how: drift.data.computed_from })}
          {drift.data.added_columns.length > 0
            ? ` ${t('profile.drift.added', { columns: drift.data.added_columns.join(', ') })}`
            : ''}
          {drift.data.removed_columns.length > 0
            ? ` ${t('profile.drift.removed', { columns: drift.data.removed_columns.join(', ') })}`
            : ''}
        </p>
      )}
    </div>
  );
}

function ColumnRow({
  sketch,
  drift,
}: {
  readonly sketch: ColumnSketch;
  readonly drift: ColumnDrift | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const distinct = distinctReading(sketch);
  const dominant = dominantReading(sketch);
  const verdict = drift === undefined ? null : driftVerdict(drift);

  return (
    <tr>
      <td>
        {sketch.column}
        {isDominated(sketch) ? (
          /* Flagged rather than sorted to the top: the table's order is the schema's order, which is
             what a reader is matching against their own DDL. A row that jumped position would be
             harder to find, not easier. */
          <span className="lq-muted" title={t('profile.dominated.hint')}>
            {' '}
            ⚠
          </span>
        ) : null}
      </td>
      <td className="lq-table__num">{count(sketch.rows_scanned)}</td>
      <td className="lq-table__num">{count(sketch.null_count)}</td>
      <td className="lq-table__num" title={t('profile.complete.hint')}>
        {share(sketch.completeness)}
      </td>
      <td className="lq-table__num">
        {distinct.value}
        {distinct.measured ? null : (
          /* `≈` alone could read as rounding. The title says which reading this is and why it
              matters, because a 3% error bar is the difference between a finding and noise. */
          <span className="lq-muted" title={t('profile.distinct.estimated')}>
            {' '}
            ~
          </span>
        )}
      </td>
      <td className="lq-table__num">
        {dominant.value}
        {dominant.measured || sketch.dominant_share === null ? null : (
          /* Bounded rather than counted — the frequent-items summary hit its retention floor, so this
              share is an upper bound. Marked separately from the distinct column because the two
              sketches have independent bounds and one can be exact while the other is not. */
          <span className="lq-muted" title={t('profile.dominant.bounded')}>
            {' '}
            ~
          </span>
        )}
      </td>
      <td>
        {verdict === null || drift === undefined ? (
          <span className="lq-muted">{t('profile.drift.notCompared')}</span>
        ) : (
          <>
            <span data-tone={DRIFT_TONE[verdict]}>
              {t(`profile.drift.verdict.${verdict}`)}
            </span>
            {drift.exact ? null : (
              /* The verdict itself was adjudicated against estimates. `ColumnDrift.exact` was on the
                 wire and read by nothing while this module claimed `exact` travels with every figure
                 — so the one figure being judged was the one with no marker. */
              <span className="lq-muted" title={t('profile.drift.estimated')}>
                {' '}
                ~
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

/* One figure above the table, with the column it is about and a line saying how to read it.
 *
 * **The note is required, not optional**, and that is the card's whole contract: every figure this
 * panel shows is either exact, estimated, or absent, and a number without that qualifier invites
 * somebody to act on an error bar. `MetricCard` was the alternative and carries no slot for it.
 */
function HighlightCard({
  label,
  value,
  note,
  muted = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly note: string;
  /** For a figure that is words rather than a number — "Not measured" reads wrong at the figure
   * weight, because it is not a measurement competing with the others. */
  readonly muted?: boolean;
}): ReactElement {
  return (
    <div className="lq-highlight">
      <p className="lq-highlight__label">{label}</p>
      <p
        className={
          muted ? 'lq-highlight__value lq-highlight__value--words' : 'lq-highlight__value'
        }
      >
        {value}
      </p>
      <p className="lq-highlight__note">{note}</p>
    </div>
  );
}

/* One column's drift verdict, as a badge and a sentence.
 *
 * A `<li>` with the column, the verdict and the explanation, rather than a table row: the sentence is
 * the actionable half — "the value set changed" against "the distribution shifted" — and it does not
 * fit a cell. `DRIFT_TONE` is a total map over the four verdicts, so a fifth cannot render in the
 * everything-is-fine tone.
 */
function DriftRow({ drift }: { readonly drift: ColumnDrift }): ReactElement {
  const { t } = useTranslation();
  const verdict = driftVerdict(drift);
  return (
    <li className="lq-driftlist__row">
      <span className="lq-driftlist__column lq-mono">{drift.column}</span>
      <StatusBadge
        tone={DRIFT_TONE[verdict]}
        label={t(`profile.drift.verdict.${verdict}`)}
        title={t(`profile.drift.meaning.${verdict}`)}
      />
      <span className="lq-driftlist__detail">
        {t(`profile.drift.sentence.${verdict}`)}
      </span>
    </li>
  );
}
